import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  AppUtil,
  environment,
  ExpirableStorage,
  SessionService,
} from '@cai-common';
import { datadogRum } from '@datadog/browser-rum';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage } from 'aws-amplify/utils';

declare let window: any;

@Injectable()
export class AppInitService {
  constructor(private sessionService: SessionService) {}

  public init() {
    const appConfigPath = AppUtil.getAppConfigPath(window.location.href);
    return from(
      fetch(appConfigPath).then(function (response) {
        return response.json();
      }),
    )
      .pipe(
        map((config) => {
          window.config = config;
          return config;
        }),
      )
      .toPromise()
      .then(() => {
        this.initAWSRum();
        this.initDatadog();
        this.initAmplify();
        this.initGoogleAnalytics();
      });
  }

  initDatadog() {
    if (environment.datadog) {
      datadogRum.init({
        applicationId: 'b83005b6-7e87-4e73-83a4-f6e4e516e815',
        clientToken: 'pub1ade47d9499c6ad40637be6af4c390f1',
        site: 'us3.datadoghq.com',
        service: 'app',
        version: environment.version,
        enableExperimentalFeatures: ['clickmap', 'feature_flags'],
        env: environment.name,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        proxy: 'https://rum.cargoai.co',
        defaultPrivacyLevel: 'allow',
        beforeSend: (event) => {
          event.view.url = AppUtil.modifyUrl(event.view.url);
          return true;
        },
      });
      datadogRum.startSessionReplayRecording();
    }
  }

  initAWSRum() {
    try {
      if (environment.awsRUM) {
        const script = document.createElement('script');
        script.defer = true;
        script.innerHTML =
          "(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement('script');z.async=true;z.src=s;document.head.insertBefore(z,document?.getElementsByTagName('script')[0]);})('cwr','4dfd2bbe-e53e-4a74-a2c6-34ceecea3345','1.0.0','ap-southeast-1','https://client.rum.us-east-1.amazonaws.com/1.0.2/cwr.js',{sessionSampleRate:1,guestRoleArn:\'arn:aws:iam::703603070493:role/RUM-Monitor-ap-southeast-1-703603070493-8014065428361-Unauth\',identityPoolId:\'ap-southeast-1:b7c2e876-836b-462b-9f1d-4f0dc3007b50\',endpoint:\'https://dataplane.rum.ap-southeast-1.amazonaws.com\',telemetries:[\'performance\',\'errors\',\'http\'],allowCookies:true,enableXRay:true});";
        document.head.appendChild(script);
      }
    } catch (e) {
      console.error(e);
    }
  }

  initGoogleAnalytics() {
    try {
      if (environment.gtmId) {
        const script = document.createElement('script');
        script.defer = true;
        script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${environment.gtmId}');`;
        document.head.appendChild(script);
      }
    } catch (e) {
      console.error(e);
    }
  }

  initAmplify() {
    const rememberMe = this.sessionService.getRememberMeLogin();
    const apis = {};
    environment.amplify.APIs.forEach((API) => {
      apis[API.name] = {
        endpoint: API.endpoint,
      };
    });

    Amplify.configure(
      {
        Auth: {
          Cognito: {
            userPoolId: environment.amplify.auth.userPoolId,
            userPoolClientId: environment.amplify.auth.userPoolWebClientId,
            allowGuestAccess: false,
            identityPoolId: '',
            loginWith: {
              oauth: environment.amplify.auth?.oauth,
            },
          },
        },
        API: {
          GraphQL: {
            apiKey: environment.amplify.aws_appsync_apiKey,
            defaultAuthMode: 'apiKey' as any,
            endpoint: environment.amplify.aws_appsync_graphqlEndpoint,
            region: environment.amplify.aws_appsync_region,
          },
          REST: apis,
        },
      },
      {
        API: {
          GraphQL: {
            headers: async () => {
              try {
                return {
                  Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken.toString()}`,
                };
              } catch {
                return null;
              }
            },
          },
          REST: {
            headers: async () => {
              try {
                return {
                  Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken.toString()}`,
                };
              } catch {
                return null;
              }
            },
          },
        },
      },
    );

    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      rememberMe ? defaultStorage : new ExpirableStorage(),
    );
  }
}
